<template>
  <div class="report-page">
    <div class="dropdown-container">
      <!-- FSM -->
      <div class="dropdown-item">
        <label for="fsmSelect">FSM</label>
        <select id="fsmSelect" v-model="selectedFsm" @change="onFsmChange" :disabled="isFsmLocked">
          <option value="">Select FSM</option>
          <option v-if="isOtherUser" :value="ALL">All FSMs</option>
          <option v-for="fsm in fsmList" :key="fsm.profile_id" :value="String(fsm.profile_id)">
            {{ fsm.name }}
          </option>
        </select>
      </div>

      <!-- ASM -->
      <div class="dropdown-item">
        <label for="asmSelect">ASM</label>
        <select id="asmSelect" v-model="selectedAsm" @change="onAsmChange" :disabled="isAsmLocked">
          <option value="">Select ASM</option>
          <option v-if="isFsmUser || isOtherUser" :value="ALL">All ASMs</option>
          <option v-for="asm in asmList" :key="asm.profile_id" :value="String(asm.profile_id)">
            {{ asm.name }}
          </option>
        </select>
      </div>

      <!-- Distributor -->
      <div class="dropdown-item">
        <label for="distributorSelect">Distributor</label>
        <select id="distributorSelect" v-model="selectedDistributor" @change="onDistributorChange"
          :disabled="isDistributorLocked">
          <option value="">Select Distributor</option>
          <option v-if="isFsmUser || isAsmUser || isOtherUser" :value="ALL">All Distributors</option>

          <option v-for="dist in distributorList" :key="dist.profile_id ?? dist.id"
            :value="String(dist.profile_id ?? dist.id)">
            {{ dist.name ?? dist.business_name ?? dist.full_name ?? ("Distributor #" + (dist.profile_id ?? dist.id)) }}
          </option>
        </select>
      </div>

      <!-- Dealer -->
      <div class="dropdown-item">
        <label for="dealerSelect">Dealer</label>
        <select id="dealerSelect" v-model="selectedDealer" @change="onDealerChange" :disabled="!canUseDealerFilter" :class="{ 'inactive-select': selectedDealerIsInactive }">
          <option value="">All Dealers</option>
          <option v-for="d in dealerList" :key="d.profile_id ?? d.id" :value="String(d.profile_id ?? d.id)" :class="{ 'inactive-option': !d.is_active }">
            {{ d.name ?? d.business_display_name ?? d.business_name ?? ("Dealer #" + (d.profile_id ?? d.id)) }}
          </option>
        </select>
      </div>

      <!-- Dates (optional) -->
      <div class="dropdown-item">
        <label>Start Date (optional)</label>
        <input type="date" v-model="startDate" :max="currentDate" @change="handleDateChange"
          @blur="validateStartDate(true)" />
      </div>

      <div class="dropdown-item">
        <label>End Date (optional)</label>
        <input type="date" v-model="endDate" :max="currentDate" @change="handleDateChange"
          @blur="validateEndDate(true)" />
      </div>

      <!-- Search & Export -->
      <div class="search-container">
        <button type="button" @click="performSearch" class="btn button-default" :disabled="loading">
          <i v-if="loading" class="fa fa-spinner fa-spin me-1"></i>
          {{ loading ? "Searching..." : "Search" }}
        </button>

        <button type="button" @click="exportToExcel" class="btn button-export" :disabled="!reportData.length || !items.length">
          Export to Excel
        </button>
      </div>
    </div>

    <div v-if="loading" class="loading-spinner mt-3">
      <i class="fa fa-spinner fa-spin"></i> Loading...
    </div>

    <!-- Table -->
    <div v-if="!loading && reportData.length && items.length" class="card mt-3">
      <div class="table-scroll">
        <table class="table table-striped table-bordered">
          <thead>
            <tr class="header-row-1">
              <th rowspan="2">FSM</th>
              <th rowspan="2">ASM</th>
              <th rowspan="2">Distributor</th>
              <th rowspan="2">Dealer</th>
              <th rowspan="2">Date</th>
              <th v-for="it in items" :key="it.item_id" class="text-center">
                {{ it.item_name }}
              </th>
              <th>Total</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="(row, i) in reportData" :key="i" :class="{ 'inactive-row': isInactiveDealer(row) }">
              <td>{{ row.fsm_name || "-" }}</td>
              <td>{{ row.asm_name || "-" }}</td>
              <td>{{ row.distributor_name || "-" }}</td>
              <td>{{ row.dealer_name || "-" }}</td>
              <td>{{ row.stock_date || "-" }}</td>

              <td v-for="it in items" :key="it.item_id" class="text-end">
                {{ formatQty(row?.[String(it.item_id)] ?? 0) }}
              </td>

              <td class="text-end"><strong>{{ formatQty(row.total_qty ?? 0) }}</strong></td>
            </tr>

            <tr v-if="reportData.length" class="total-row">
              <td colspan="5" class="text-end"><strong>Total</strong></td>
              <td v-for="it in items" :key="it.item_id" class="text-end">
                <strong>{{ formatQty(columnTotal(String(it.item_id))) }}</strong>
              </td>
              <td class="text-end"><strong>{{ formatQty(grandTotalQty) }}</strong></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="!loading && !reportData.length" class="no-data-message">
      <p>No data found. Please select filters and click Search.</p>
    </div>
  </div>
</template>

<script>
import * as XLSX from "xlsx";
import { useToast } from "vue-toastification";

import {
  fetchFsmList,
  fetchAsmList,
  fetchDistributorList,
  fetchDealerListManagementTrue, // ✅ IMPORTANT: use the SAME dealer list API that works in sales page
  fetchDistributorWiseDealerStockReport,
} from "@/services/dataService";

export default {
  name: "DistributorWiseDealerStockForm",

  data() {
    const ALL = "__all__";
    const currentDate = new Date().toISOString().split("T")[0];

    return {
      token: "",

      fsmList: [],
      asmList: [],
      distributorList: [],
      dealerList: [],

      selectedFsm: "",
      selectedAsm: "",
      selectedDistributor: "",
      selectedDealer: "",

      // optional dates
      startDate: "",
      endDate: "",
      currentDate,

      loading: false,
      items: [],
      reportData: [],

      userLabel: "",
      roleKey: "OTHER",
      myProfileId: Number(localStorage.getItem("profile_id") || 0),

      // do NOT auto-lock FSM/ASM; only distributor locked for distributor login
      isFsmLocked: false,
      isAsmLocked: false,
      isDistributorLocked: false,

      ALL,
      redirecting: false,
    };
  },

  computed: {
    isDistributorUser() {
      return this.roleKey === "DIS";
    },
    isAsmUser() {
      return this.roleKey === "ASM";
    },
    isFsmUser() {
      return this.roleKey === "FSM";
    },
    isOtherUser() {
      return this.roleKey === "OTHER";
    },

    canUseDealerFilter() {
      return !!this.selectedDistributor && this.selectedDistributor !== this.ALL;
    },

    grandTotalQty() {
      return this.reportData.reduce((sum, r) => sum + (Number(r.total_qty) || 0), 0);
    },

    selectedDealerIsInactive() {
      const d = this.dealerList.find(
        (x) => String(x.profile_id) === String(this.selectedDealer),
      );
      return !!d && !d.is_active;
    },
  },

  mounted() {
    this.initLoginAndLoadFsm();
  },

  methods: {
    toast(type, message) {
      const t = useToast();
      type === "error"
        ? t.error(message, { position: "top-right", timeout: 3000 })
        : t.success(message, { position: "top-right", timeout: 3000 });
    },

    isValidISODate(dateStr) {
      if (!dateStr || typeof dateStr !== "string") return false;

      // block manual formats like 2025/11/31
      if (dateStr.includes("/")) return false;

      const m = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (!m) return false;

      const y = Number(m[1]);
      const mo = Number(m[2]);
      const d = Number(m[3]);

      // block overflow dates like 2025-11-31
      const dt = new Date(y, mo - 1, d);
      return dt.getFullYear() === y && dt.getMonth() === mo - 1 && dt.getDate() === d;
    },

    validateStartDate(showToast = true) {
      if (!this.startDate) return true; // optional

      if (!this.isValidISODate(this.startDate)) {
        if (showToast) this.toast("error", "Invalid Start Date");
        this.startDate = "";
        return false;
      }

      // ISO string compare works (YYYY-MM-DD)
      if (this.startDate > this.currentDate) {
        if (showToast) this.toast("error", "Start Date cannot be in the future");
        this.startDate = "";
        return false;
      }

      return true;
    },

    validateEndDate(showToast = true) {
      if (!this.endDate) return true; // optional

      if (!this.isValidISODate(this.endDate)) {
        if (showToast) this.toast("error", "Invalid End Date");
        this.endDate = "";
        return false;
      }

      if (this.endDate > this.currentDate) {
        if (showToast) this.toast("error", "End Date cannot be in the future");
        this.endDate = "";
        return false;
      }

      return true;
    },

    handleDateChange() {
      // validate each field silently first
      this.validateStartDate(false);
      this.validateEndDate(false);

      // if both exist, check range
      if (this.startDate && this.endDate && this.endDate < this.startDate) {
        this.toast("error", "Invalid date range picked.");
        this.endDate = "";
      }
    },

    // ✅ Optional dates rule:
    // - if both empty => OK
    // - if one filled => require both
    // - if both filled => validate + range
    validateOptionalDatesForSearch() {
      const hasStart = !!this.startDate;
      const hasEnd = !!this.endDate;

      if (!hasStart && !hasEnd) return true;

      if (hasStart && !hasEnd) { this.toast("error", "Please select End Date"); return false; }
      if (!hasStart && hasEnd) { this.toast("error", "Please select Start Date"); return false; }

      if (!this.validateStartDate(true)) return false;
      if (!this.validateEndDate(true)) return false;

      if (this.endDate < this.startDate) {
        this.toast("error", "Invalid date range picked.");
        return false;
      }

      return true;
    },


    // ---------- auth helpers ----------
    bodyOf(x) {
      return x?.data ?? x;
    },
    msgOf(x) {
      const b = this.bodyOf(x?.response?.data ?? x);
      return String(b?.message ?? b?.error ?? x?.message ?? "");
    },
    isUnauthorizedAny(x) {
      const http = x?.response?.status ?? x?.status;
      const msg = this.msgOf(x).toLowerCase();

      const authMsg =
        /access token is invalid|unauthenticated|token.*expired|expired token|invalid token|session.*expired/;
      if (http === 401) return authMsg.test(msg);
      return http === 419 || http === 440 || http === 498 || authMsg.test(msg);
    },
    redirectToLogin(message) {
      if (this.redirecting) return;
      this.redirecting = true;

      this.toast("error", message || "Session expired. Please login again.");

      localStorage.removeItem("token_set");
      localStorage.removeItem("profile_id");
      localStorage.removeItem("user_label");
      localStorage.removeItem("userLabel");
      localStorage.removeItem("role");

      if (this.$router) this.$router.replace("/login");
      else window.location.replace("/login");
    },
    handleUnauthorized(x) {
      if (!this.isUnauthorizedAny(x)) return false;
      this.redirectToLogin();
      return true;
    },

    // ---------- role ----------
    detectRoleKeyFromStorage(labelRaw) {
      const u = String(labelRaw || "").toUpperCase().trim();
      if (u === "DISTRIBUTOR" || u.startsWith("DISTRIBUTOR") || u === "DIS" || u.startsWith("DIS_")) return "DIS";
      if (u === "ASM" || u.startsWith("ASM_")) return "ASM";
      if (u === "FSM" || u.startsWith("FSM_")) return "FSM";
      return "OTHER";
    },

    // ---------- validation ----------
    validateFiltersOneByOne() {
      if (!this.selectedFsm) {
        this.toast("error", "Please select FSM.");
        return false;
      }
      if (!this.selectedAsm) {
        this.toast("error", "Please select ASM.");
        return false;
      }
      if (!this.selectedDistributor) {
        this.toast("error", "Please select Distributor.");
        return false;
      }
      // dealer optional
      return true;
    },

    // ---------- init (NO prefill except distributor for distributor login) ----------
    async initLoginAndLoadFsm() {
      this.loading = true;
      try {
        const token = localStorage.getItem("token_set") || "";
        if (!token) return this.redirectToLogin("Session expired. Please login again.");

        this.token = token;
        this.myProfileId = Number(localStorage.getItem("profile_id") || 0);

        const label =
          localStorage.getItem("user_label") ||
          localStorage.getItem("userLabel") ||
          localStorage.getItem("role") ||
          "";

        this.userLabel = String(label);
        this.roleKey = this.detectRoleKeyFromStorage(this.userLabel);

        await this.loadFsmList();
        if (this.redirecting) return;

        // reset (NO auto select)
        this.selectedFsm = "";
        this.selectedAsm = "";
        this.selectedDistributor = "";
        this.selectedDealer = "";

        this.asmList = [];
        this.distributorList = [];
        this.dealerList = [];

        this.isDistributorLocked = false;

        this.items = [];
        this.reportData = [];
      } catch (err) {
        if (this.handleUnauthorized(err)) return;
        console.error(err);
        this.toast("error", "Failed to initialize page.");
      } finally {
        this.loading = false;
      }
    },

    // ---------- dropdown loaders ----------
    async loadFsmList() {
      try {
        const profileId = localStorage.getItem("profile_id");
        const res = await fetchFsmList(profileId, this.token);
        if (this.handleUnauthorized(res)) return;

        // supports both res.data.data and res.data
        const body = this.bodyOf(res);
        this.fsmList = body?.data || body?.data?.data || body?.data?.data || [];
      } catch (err) {
        if (this.handleUnauthorized(err)) return;
        console.error(err);
        this.toast("error", "Failed to load FSM list.");
      }
    },

    async onFsmChange() {
      this.selectedAsm = "";
      this.selectedDistributor = "";
      this.selectedDealer = "";

      this.asmList = [];
      this.distributorList = [];
      this.dealerList = [];

      this.items = [];
      this.reportData = [];

      if (this.selectedFsm === this.ALL) return;
      if (!this.selectedFsm) return;

      await this.loadAsmList();
    },

    async loadAsmList() {
      try {
        const profileId = localStorage.getItem("profile_id");
        const res = await fetchAsmList(profileId, this.selectedFsm, this.token);
        if (this.handleUnauthorized(res)) return;

        const body = this.bodyOf(res);
        this.asmList = body?.data || body?.data?.data || [];
      } catch (err) {
        if (this.handleUnauthorized(err)) return;
        console.error(err);
        this.toast("error", "Failed to load ASM list.");
      }
    },

    async onAsmChange() {
      this.selectedDistributor = "";
      this.selectedDealer = "";

      this.distributorList = [];
      this.dealerList = [];

      this.isDistributorLocked = false;

      this.items = [];
      this.reportData = [];

      if (this.selectedAsm === this.ALL) return;
      if (!this.selectedAsm) return;

      await this.loadDistributorList();
    },

    async loadDistributorList() {
      try {
        const profileId = localStorage.getItem("profile_id");
        const res = await fetchDistributorList(profileId, this.selectedAsm, this.token);
        if (this.handleUnauthorized(res)) return;

        const body = this.bodyOf(res);
        let list = body?.data || body?.data?.data || [];

        // ✅ ONLY distributor prefill for distributor login
        if (this.isDistributorUser && this.myProfileId) {
          const mine = list.find((d) => Number(d.profile_id ?? d.id) === Number(this.myProfileId));
          if (mine) {
            list = [mine];
            this.selectedDistributor = String(mine.profile_id ?? mine.id);
            this.isDistributorLocked = true;

            // programmatic set doesn't trigger @change
            await this.loadDealerList();
          }
        }

        this.distributorList = list;
      } catch (err) {
        if (this.handleUnauthorized(err)) return;
        console.error(err);
        this.toast("error", "Failed to load Distributor list.");
      }
    },

    async onDistributorChange() {
      this.selectedDealer = "";
      this.dealerList = [];

      this.items = [];
      this.reportData = [];

      if (!this.selectedDistributor) return;
      if (this.selectedDistributor === this.ALL) return;

      await this.loadDealerList();
    },

    async loadDealerList() {
      this.dealerList = [];
      this.selectedDealer = "";

      if (!this.selectedDistributor || this.selectedDistributor === this.ALL) return;

      try {
        // ✅ This is the key fix: use the same working dealer endpoint used in the sales page
        const profileId = localStorage.getItem("profile_id");
        const res = await fetchDealerListManagementTrue(profileId, this.selectedDistributor, true, this.token);
        if (this.handleUnauthorized(res)) return;

        const body = this.bodyOf(res);
        const raw = body?.data || body?.data?.data || [];

        // normalize shape
        this.dealerList = raw.map((d) => ({
          profile_id: d.profile_id ?? d.id,
          name: d.name ?? d.business_display_name ?? d.business_name ?? d.full_name ?? ("Dealer #" + (d.profile_id ?? d.id)),
          is_active: d.is_active == null ? true : Number(d.is_active) !== 0,
        }));
      } catch (err) {
        if (this.handleUnauthorized(err)) return;
        console.error(err);
        this.toast("error", "Failed to load Dealer list.");
      }
    },

    onDealerChange() {
      this.items = [];
      this.reportData = [];
    },

    // ---------- report ----------
    formatQty(v) {
      const n = Number(v);
      if (!Number.isFinite(n)) return "0";
      return n.toLocaleString("en-US", { maximumFractionDigits: 0 });
    },

    isInactiveDealer(row) {
      return row?.dealer_is_active != null && Number(row.dealer_is_active) === 0;
    },

    columnTotal(itemIdStr) {
      return this.reportData.reduce((sum, r) => sum + (Number(r?.[itemIdStr]) || 0), 0);
    },

    async performSearch() {
      this.token = localStorage.getItem("token_set") || "";
      if (!this.token) return this.redirectToLogin();

      if (!this.validateFiltersOneByOne()) return;

      if (!this.validateOptionalDatesForSearch()) return;


      this.loading = true;
      this.items = [];
      this.reportData = [];

      try {
        const payload = {
          start_date: this.startDate || null,
          end_date: this.endDate || null,
          fsm_profile_id: this.selectedFsm === this.ALL ? null : this.selectedFsm,
          asm_profile_id: this.selectedAsm === this.ALL ? null : this.selectedAsm,
          distributor_profile_id: this.selectedDistributor === this.ALL ? null : this.selectedDistributor,
          dealer_profile_id: this.selectedDealer ? this.selectedDealer : null,
        };

        const apiRes = await fetchDistributorWiseDealerStockReport(this.token, payload);
        if (this.handleUnauthorized(apiRes)) return;

        const res = this.bodyOf(apiRes);
        if (!res || res.status !== true) {
          this.toast("error", res?.message || "Error loading report.");
          return;
        }

        const items = Array.isArray(res.items) ? res.items : [];
        const rows = Array.isArray(res.data) ? res.data : [];

        this.items = items
          .map((x) => ({
            item_id: String(x.item_id),
            item_name: String(x.item_name ?? ""),
            sort_id: Number(x.sort_id ?? 0),
          }))
          .sort((a, b) => a.sort_id - b.sort_id);

        this.reportData = rows.map((r) => {
          const out = { ...r };
          this.items.forEach((it) => {
            const k = String(it.item_id);
            out[k] = Number(r?.[k]) || 0;
          });
          out.total_qty = Number(r?.total_qty) || 0;
          return out;
        });

        if (!this.reportData.length) {
          this.toast("error", "No data found for selected filters.");
          return;
        }

        this.toast("success", "Report Generated successfully.");
      } catch (err) {
        if (this.handleUnauthorized(err)) return;
        console.error(err?.response?.data || err);
        this.toast("error", err?.response?.data?.message || "Error loading report.");
      } finally {
        this.loading = false;
      }
    },

    // ---------- excel ----------
    exportToExcel() {
      if (!this.reportData.length || !this.items.length) {
        this.toast("error", "No data to export.");
        return;
      }

      const getName = (list, id) => {
        if (!id || id === this.ALL) return "All";
        const item = list.find(x => String(x.profile_id ?? x.id) === String(id));
        return item ? (item.name ?? item.business_name ?? "-") : "-";
      };

      const filterRows = [
        ["Filters"],
        ["FSM", getName(this.fsmList, this.selectedFsm)],
        ["ASM", getName(this.asmList, this.selectedAsm)],
        ["Distributor", getName(this.distributorList, this.selectedDistributor)],
        ["Dealer", this.selectedDealer ? getName(this.dealerList, this.selectedDealer) : "All"],
        ["Start Date", this.startDate || "-"],
        ["End Date", this.endDate || "-"],
        []
      ];

      const headerRow = [
        "FSM",
        "ASM",
        "Distributor",
        "Dealer",
        "Date",
        ...this.items.map((it) => it.item_name),
        "Total",
      ];

      const dataRows = this.reportData.map((r) => [
        r.fsm_name || "-",
        r.asm_name || "-",
        r.distributor_name || "-",
        r.dealer_name || "-",
        r.stock_date || "-",
        ...this.items.map((it) => Number(r[String(it.item_id)]) || 0),
        Number(r.total_qty) || 0,
      ]);

      const totalRow = [
        "TOTAL",
        "",
        "",
        "",
        "",
        ...this.items.map((it) => this.columnTotal(String(it.item_id))),
        this.grandTotalQty,
      ];

      const aoa = [...filterRows, headerRow, ...dataRows, totalRow];
      const ws = XLSX.utils.aoa_to_sheet(aoa);

      // ===== GENERATED SECTION =====
      const firstName = localStorage.getItem("first_name") || "";
      const lastName = localStorage.getItem("last_name") || "";
      const userFullName = `${firstName} ${lastName}`.trim() || "-";
      const generatedAt = new Date().toLocaleString();

      const generatedRows = [
        [],
        ["Generated By", userFullName],
        ["Generated At", generatedAt]
      ];

      const tableEndRow = filterRows.length + dataRows.length + 3;
      XLSX.utils.sheet_add_aoa(ws, generatedRows, { origin: `A${tableEndRow}` });

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Dealer_Wise_Stock");
      XLSX.writeFile(wb, `Dealer_Wise_Stock_${new Date().toISOString().slice(0, 10)}.xlsx`);
      this.toast("success", "Excel exported successfully.");
    },
  },
};
</script>

<style scoped>
.table-scroll {
  max-height: 500px;
  overflow-y: auto;
  overflow-x: auto;
  border: 1px solid #ddd;
  margin-top: 10px;
  position: relative;
}

.table-scroll table {
  min-width: 100%;
}

.container {
  background-color: #ffffff;
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  padding: 20px;
  margin: 10px;
  min-height: calc(100vh - 20px);
  width: calc(100% - 20px);
  box-sizing: border-box;
}

.dropdown-container {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 15px;
  width: 100%;
  justify-content: center;
  margin-bottom: 20px;
}

.dropdown-item {
  min-width: 180px;
  max-width: 250px;
}

label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
  font-size: 14px;
}

select,
input[type="date"] {
  width: 100%;
  padding: 10px;
  border-radius: 5px;
  border: 1px solid #ccc;
  font-size: 14px;
}

.search-container {
  display: flex;
  align-items: flex-end;
  justify-content: left;
}

.btn.button-default {
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  background-color: #007bff;
  color: #ffffff;
  font-size: 14px;
  cursor: pointer;
}

.btn.button-default:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
}

.btn.button-export {
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  background-color: #28a745;
  color: #ffffff;
  font-size: 14px;
  cursor: pointer;
}

.btn.button-export:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
}

.loading-spinner {
  font-size: 18px;
  text-align: center;
  margin-top: 20px;
  color: #007bff;
}

.table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}

.table thead th {
  background-color: #f9f9f9;
  font-weight: bold;
  z-index: 3;
}

.table thead .header-row-1 th {
  position: sticky;
  top: 0;
}

.table tbody tr.inactive-row td {
  background-color: #ffebee !important;
}

.table th,
.table td {
  padding: 12px;
  border-bottom: 1px solid #ddd;
  white-space: nowrap;
}

.text-center {
  text-align: center;
}

.text-end {
  text-align: right;
}

.total-row td {
  background: #f1f3f5;
  font-weight: 700;
}

.no-data-message {
  text-align: center;
  padding: 40px;
  font-size: 16px;
  color: #666;
}

.mt-3 {
  margin-top: 1rem;
}

.mt-4 {
  margin-top: 1.5rem;
}

/* Normal options always look normal */
.dropdown-item select option {
  background-color: #ffffff;
  color: #212529;
}

/* Inactive dealer: dropdown options */
.dropdown-item select option.inactive-option {
  background-color: #ffebee;
}

/* Select box when an inactive dealer is the selected value */
.dropdown-item select.inactive-select {
  background-color: #ffebee;
}
</style>
